import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import ListWrestlerMobileRender from '../../components/MobileComponents/ListaLottatoriPage/ListWrestlerMobileRender';

function ListWrestlersMobile() {
    const loaderData = useLoaderData() as any;
    const initialWrestlers = loaderData?.data?.data ?? [];

    const [allWrestlers] = useState(initialWrestlers);
    const [filteredWrestlers, setFilteredWrestlers] = useState(initialWrestlers);
    const [currentPage, setCurrentPage] = useState(1);
    const wrestlersPerPage = 20;

    const totalPages = useMemo(
        () => Math.max(1, Math.ceil(filteredWrestlers.length / wrestlersPerPage)),
        [filteredWrestlers.length]
    );

    const currentPageWrestlers = useMemo(() => {
        const safePage = Math.min(currentPage, totalPages);
        const start = (safePage - 1) * wrestlersPerPage;
        return filteredWrestlers.slice(start, start + wrestlersPerPage);
    }, [filteredWrestlers, currentPage, totalPages]);

    const [orderBy, setOrderBy] = useState("name");
    const [isOrderByName, setIsOrderByName] = useState(true);
    const [isOrderByHeight, setIsOrderByHeight] = useState(false);
    const [isOrderByWeight, setIsOrderByWeight] = useState(false);
    const [isOrderDesc, setIsOrderDesc] = useState(false);
    const [isFilterMan, setIsFilterMan] = useState(false);
    const [isFilterWomen, setIsFilterWomen] = useState(false);
    const [isFilterManager, setIsFilterManager] = useState(false);
    const [searchText, setSearchText] = useState('');


    // Funzione per cercare specifico Wrestler
    async function updateListWrestlerWithSearchBar(value) {
        setSearchText(value);
        // Filtra i wrestlers già caricati invece di fare una nuova chiamata API
        filterWrestlers(value, isFilterMan, isFilterWomen, isFilterManager, orderBy, isOrderDesc);
    }

    // Funzione per filtrare i wrestlers lato client
    function filterWrestlers(searchValue = '', filterMan = isFilterMan, filterWomen = isFilterWomen, filterManager = isFilterManager, order = orderBy, orderDesc = isOrderDesc) {
        let filtered = [...allWrestlers];

        // Filtra per ricerca
        if (searchValue) {
            filtered = filtered.filter(wrestler => 
                wrestler.name.toLowerCase().includes(searchValue.toLowerCase())
            );
        }

        // Filtra per genere/ruolo
        if (filterMan) {
            filtered = filtered.filter(wrestler => wrestler.is_female == false);
        }
        if (filterWomen) {
            filtered = filtered.filter(wrestler => wrestler.is_female == true);
        }
        if (filterManager) {
            filtered = filtered.filter(wrestler => wrestler.is_manager == true);
        }

        // Ordina — attivi sempre prima, poi criterio secondario
        filtered.sort((a, b) => {
            const activeA = a.is_active ? 0 : 1;
            const activeB = b.is_active ? 0 : 1;
            if (activeA !== activeB) return activeA - activeB;

            let aValue, bValue;
            switch (order) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
                    break;
                case 'height':
                    aValue = parseFloat(a.height) || 0;
                    bValue = parseFloat(b.height) || 0;
                    break;
                case 'weight':
                    aValue = parseFloat(a.weight) || 0;
                    bValue = parseFloat(b.weight) || 0;
                    break;
                default:
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();
            }

            if (orderDesc) {
                return bValue > aValue ? 1 : -1;
            } else {
                return aValue > bValue ? 1 : -1;
            }
        });

        setFilteredWrestlers(filtered);
        setCurrentPage(1);
    }

    // Funzione per gestire i cambiamenti di pagina (ora lato client)
    function updateDataByPage(page) {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    }

    // Funzione per gestire i cambiamenti di ordinamento (ora lato client)
    function updateDataByOrder(newFilterMan, newFilterWomen, newFilterManager, newOrderBy, newIsOrderDesc) {
        // Aggiorna gli stati
        setIsFilterMan(newFilterMan);
        setIsFilterWomen(newFilterWomen);
        setIsFilterManager(newFilterManager);
        setOrderBy(newOrderBy);
        setIsOrderDesc(newIsOrderDesc);
        
        // Filtra e ordina lato client con i nuovi valori
        filterWrestlers(searchText, newFilterMan, newFilterWomen, newFilterManager, newOrderBy, newIsOrderDesc);
    }

    // Funzione per cambiare la pagina
    function handlePageChange(page) {
        updateDataByPage(page);
    }

    // Funzione per gestire i cambiamenti di ordinamento
    function handleOrderChange(order) {
        // Aggiorna gli stati di ordinamento
        if(order === 'name') {
            setIsOrderByName(true);
            setIsOrderByHeight(false);
            setIsOrderByWeight(false);
        } else if(order === 'height') {
            setIsOrderByName(false);
            setIsOrderByHeight(true);
            setIsOrderByWeight(false);
        } else if(order === 'weight') {
            setIsOrderByName(false);
            setIsOrderByHeight(false);
            setIsOrderByWeight(true);
        }

        // Aggiorna i dati lato client con il nuovo ordinamento
        updateDataByOrder(isFilterMan, isFilterWomen, isFilterManager, order, isOrderDesc);
    }

    // Funzione per gestire i cambiamenti di filtro
    function handleFilterChange(filterType) {
        let newFilterMan = isFilterMan;
        let newFilterWomen = isFilterWomen;
        let newFilterManager = isFilterManager;

        switch (filterType) {
            case 'man':
                newFilterMan = !isFilterMan;
                break;
            case 'woman':
                newFilterWomen = !isFilterWomen;
                break;
            case 'manager':
                newFilterManager = !isFilterManager;
                break;
            default:
                break;
        }

        updateDataByOrder(newFilterMan, newFilterWomen, newFilterManager, orderBy, isOrderDesc);
    }


    // Funzione per gestire i cambiamenti di ordinamento
    function handleOrderDescChange(newIsOrderDesc) {
        updateDataByOrder(isFilterMan, isFilterWomen, isFilterManager, orderBy, newIsOrderDesc);
    }

    // Funzione per gestire i cambiamenti di ricerca
    function handleSearchChange(value) {
        // Aggiorna la lista
        updateListWrestlerWithSearchBar(value);
    }


    return (
        <ListWrestlerMobileRender 
            listWrestlers={currentPageWrestlers} 
            isLoading={false}
            maxPageApi={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            isOrderByName={isOrderByName}
            isOrderByHeight={isOrderByHeight}
            isOrderByWeight={isOrderByWeight}
            isFilterMan={isFilterMan}
            isFilterWomen={isFilterWomen}
            isFilterManager={isFilterManager}
            isOrderDesc={isOrderDesc}
            onOrderChange={handleOrderChange}
            onFilterChange={handleFilterChange}
            onOrderDescChange={handleOrderDescChange}
            onSearchChange={handleSearchChange}
        />
    );
}

export default ListWrestlersMobile;
